import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Chip, IconButton, Drawer, TextField, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { ArrowLeft, Check, X, Trash2, Edit, ListChecks } from 'lucide-react';
import { useCampaignListDetails, useUpdateCampaignList, useDeleteCampaignList, useUpdateMemberStatus, useRemoveListMembers } from '@lingaai/shared-hooks';
import { formatDate } from '@lingaai/shared-utils';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function ListDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCriteria, setEditCriteria] = useState('');

  const { data, isLoading } = useCampaignListDetails(id);
  const updateList = useUpdateCampaignList();
  const deleteList = useDeleteCampaignList();
  const updateMemberStatus = useUpdateMemberStatus();
  const removeMembers = useRemoveListMembers();

  const list = data?.list;
  const members = data?.members || [];

  const handleOpenDrawer = () => {
    if (list) {
      setEditName(list.name);
      setEditDescription(list.description || '');
      setEditCriteria(list.criteria ? JSON.stringify(list.criteria, null, 2) : '');
      setDrawerOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!id || !editName.trim()) return;
    let criteriaObj: Record<string, unknown> | undefined;
    if (editCriteria.trim()) {
      try {
        criteriaObj = JSON.parse(editCriteria);
      } catch {
        return; // Invalid JSON
      }
    }
    await updateList.mutateAsync({
      id,
      data: { name: editName.trim(), description: editDescription.trim() || undefined, criteria: criteriaObj },
    });
    setDrawerOpen(false);
  };

  const handleDeleteList = async () => {
    if (!id) return;
    if (window.confirm('Are you sure you want to delete this list?')) {
      await deleteList.mutateAsync(id);
      navigate('/lists');
    }
  };

  const getSelectedNodeIds = () => {
    return selectedRows
      .map((rowId) => {
        const member = members.find((m) => m.id === rowId);
        return member?.node_id;
      })
      .filter(Boolean) as string[];
  };

  const handleApproveSelected = async () => {
    if (!id || selectedRows.length === 0) return;
    await updateMemberStatus.mutateAsync({ listId: id, nodeIds: getSelectedNodeIds(), status: 'active' });
    setSelectedRows([]);
  };

  const handleRemoveSelected = async () => {
    if (!id || selectedRows.length === 0) return;
    await updateMemberStatus.mutateAsync({ listId: id, nodeIds: getSelectedNodeIds(), status: 'removed', removalReason: 'manual' });
    setSelectedRows([]);
  };

  const handleDeleteSelected = async () => {
    if (!id || selectedRows.length === 0) return;
    if (!window.confirm('Permanently delete these members from the list?')) return;
    await removeMembers.mutateAsync({ listId: id, nodeIds: getSelectedNodeIds() });
    setSelectedRows([]);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => {
        const props = row.node?.props || {};
        if (props.first_name || props.last_name) {
          return `${props.first_name || ''} ${props.last_name || ''}`.trim();
        }
        return props.name || 'Unknown';
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.node?.props?.email || '',
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 180,
      valueGetter: (_, row) => row.node?.props?.title || '',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'added_at',
      headerName: 'Added',
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">List not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 2.5,
          mb: 2,
          background: (theme) =>
            theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(45,55,72,1) 0%, rgba(26,32,44,1) 100%)' : 'linear-gradient(135deg, rgba(247,250,252,1) 0%, rgba(237,242,247,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#10B981' }}>
                <ListChecks size={ICON_SIZES.xxl} strokeWidth={ICON_STROKE} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" fontWeight={600} sx={{ fontSize: '2rem' }}>
                    {list.name}
                  </Typography>
                  <Chip label={list.list_type === 'dynamic' ? 'Dynamic' : 'Static'} size="small" color={list.list_type === 'dynamic' ? 'secondary' : 'default'} variant="outlined" />
                </Box>
                {list.description && (
                  <Typography variant="body2" color="text.secondary">
                    {list.description}
                  </Typography>
                )}
                {list.list_type === 'dynamic' && list.criteria && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                    Criteria: {JSON.stringify(list.criteria)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<ArrowLeft size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={() => navigate('/lists')}>
              Back
            </Button>
            <Button variant="outlined" size="small" startIcon={<Edit size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleOpenDrawer}>
              Edit
            </Button>
            <Button variant="outlined" size="small" color="error" startIcon={<Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleDeleteList}>
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>

      {selectedRows.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">{selectedRows.length} selected</Typography>
          <Button size="small" variant="contained" color="success" startIcon={<Check size={16} />} onClick={handleApproveSelected} disabled={updateMemberStatus.isPending}>
            Approve
          </Button>
          <Button size="small" variant="outlined" color="warning" startIcon={<X size={16} />} onClick={handleRemoveSelected} disabled={updateMemberStatus.isPending}>
            Remove
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<Trash2 size={16} />} onClick={handleDeleteSelected} disabled={removeMembers.isPending}>
            Delete
          </Button>
        </Paper>
      )}

      <Paper sx={{ height: 'calc(100vh - 250px)' }}>
        <DataGrid
          rows={members}
          columns={columns}
          checkboxSelection
          rowSelectionModel={{ type: 'include', ids: new Set(selectedRows) }}
          onRowSelectionModelChange={(model) => setSelectedRows(Array.from(model.ids) as string[])}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          disableRowSelectionOnClick
          rowHeight={52}
          onRowClick={(params) => {
            const nodeId = params.row.node?.id;
            if (nodeId) navigate(`/people/${nodeId}`);
          }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 450, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Edit List</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="List Name" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth autoFocus />
            <TextField label="Description" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} fullWidth multiline rows={2} />
            {list?.list_type === 'dynamic' && (
              <TextField
                label="Criteria (JSON)"
                value={editCriteria}
                onChange={(e) => setEditCriteria(e.target.value)}
                fullWidth
                multiline
                rows={4}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => setDrawerOpen(false)} fullWidth>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={!editName.trim() || updateList.isPending} fullWidth>
              Save
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
